import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getSelection } from "@/lib/centra/selectionFunctions";

import usePersistedStore from "@/lib/stateManagement/persistedState/persistedStore";
import { CentraSelection } from "@frend-digital/centra-legacy/schemas/selection/selectionSchema";
import { BaseSelectionResponse } from "@frend-digital/centra-types/selection";
import { useCallback } from "react";
import formatSelection from "../../formatters/formatSelection";

export default function useSelection() {
  const setToken = usePersistedStore(state => state.setToken);

  const selection = useQuery({
    queryKey: ["selection"],
    queryFn: ({ signal }) =>
      getSelection(usePersistedStore.getState().token, setToken, signal),
  });

  const items = selection.data?.selection?.items ?? [];
  const summary = selection.data?.selection?.summary;
  const totals = selection.data?.selection?.totals;
  const priceList = selection?.data?.location?.pricelist;
  const loggedIn = selection?.data?.loggedIn;
  const shippingMethods = selection?.data?.shippingMethods;
  const cart = {
    items: items,
    summary: summary,
    totals,
    priceList: priceList,
    loggedIn: loggedIn,
    shippingMethods: shippingMethods,
    PromoCodes: selection.data?.selection?.discounts,
  };

  const selectionResponse = {
    data: selection.data,
    address: selection.data?.selection.address,
    cart,
    isLoading: selection.isLoading,
    refetch: selection.refetch,
    isFetching: selection.isFetching,
    isFetched: selection.isFetched,
    loggedIn,
  };

  return selectionResponse;
}

export type UseSelectionReturn = Awaited<ReturnType<typeof getSelection>>;

export type Cart = ReturnType<typeof useSelection>["cart"];

export const useSetSelection = () => {
  const queryClient = useQueryClient();
  const invalidateSelection = useInvalidateSelection();

  return useCallback(
    (selection: BaseSelectionResponse, invalidate?: boolean) => {
      queryClient.setQueryData(
        ["selection"],
        // Note, hacky type casting here to avoid clash between selection definitions from Checkout package and Centra workspace.
        // This would be a real bugbear to fix, and they should both technically be correct enough not to cause issues.
        formatSelection(selection as CentraSelection),
      );
      if (invalidate) {
        invalidateSelection();
      }
    },
    [queryClient, invalidateSelection],
  );
};

export const useInvalidateSelection = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["selection"],
    });
  }, [queryClient]);
};
